import React, { FC, useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  filterWhatsNext,
  getDefaultWhatsNextFinderFilters,
  getFiltersFromSearchParams,
  setFiltersInSearchParams,
} from "../../utils/whatsnext";
import { WhatsNextFinderFiltersFormTypes } from "./types";
import { WhatsNextFinderHeader, WhatsNextFinderHeaderProps } from "./whats-next-finder-header";
import { WhatsNextGrid, WhatsNextGridProps } from "./whats-next-grid";

export interface WhatsNextFinderProps {
  whatsNextItems: Queries.WhatsNextDataFragment[];
  labels: WhatsNextGridProps["labels"] & WhatsNextFinderHeaderProps["labels"];
  filterSearchParamKey: string;
}

export const WhatsNextFinder: FC<WhatsNextFinderProps> = ({
  whatsNextItems,
  labels,
  filterSearchParamKey,
}) => {
  const [isFiltersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<WhatsNextFinderFiltersFormTypes>({
    ...getDefaultWhatsNextFinderFilters(),
    ...getFiltersFromSearchParams(filterSearchParamKey),
  });

  const filtersForm = useForm<WhatsNextFinderFiltersFormTypes>({
    defaultValues: appliedFilters,
  });

  const [filteredWhatsNext, setFilteredWhatsNext] = useState<Queries.WhatsNextDataFragment[]>([]);
  const [isLoading, setLoading] = useState<WhatsNextGridProps["loading"]>(true);

  // on submit form, save submitted filters in a dedicated state
  // this lets us reset the form to its previous submitted state if the user
  // closes the panel without submitting (so we avoid re-opening it in a dirty state)
  const onSubmit = useCallback((values: WhatsNextFinderFiltersFormTypes) => {
    setAppliedFilters(() => ({ ...values }));
    setFiltersPanelOpen(false);
  }, []);

  const { reset } = filtersForm;
  // Reset filters form on close panel
  useEffect(() => {
    if (!isFiltersPanelOpen) reset(appliedFilters);
    setFiltersInSearchParams(appliedFilters, filterSearchParamKey);
  }, [isFiltersPanelOpen, appliedFilters, reset, filterSearchParamKey]);

  // Filter whatsNextItems by submitted filters
  useEffect(() => {
    setLoading(true);
    setFilteredWhatsNext(filterWhatsNext(whatsNextItems, appliedFilters));
    setLoading(false);
  }, [whatsNextItems, appliedFilters]);

  return (
    <form onSubmit={filtersForm.handleSubmit(onSubmit)}>
      <FormProvider {...filtersForm}>
        <div className="p-8 lg:p-14 flex flex-col gap-4">
          <WhatsNextFinderHeader
            whatsNextItems={whatsNextItems}
            appliedFilters={appliedFilters}
            labels={labels}
            {...{ isFiltersPanelOpen, setFiltersPanelOpen, setAppliedFilters }}
          />
          <WhatsNextGrid whatsNextItems={filteredWhatsNext} labels={labels} loading={isLoading} />
        </div>
      </FormProvider>
    </form>
  );
};
